import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isApiError, tournamentApi } from "@bw/shared/api";
import type {
  EntryCreate,
  TournamentPlayerRegistrationResponse,
} from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The player REGISTER mutation (S1) — POST /events/{event_id}/register via
 * tournamentApi.register, body the EntryCreate VERBATIM
 * ({ draw_id, expected_quote, partner_id?, payment_method_id? }). The quote is
 * server-issued and forwarded unchanged; the hook never computes a fee. MONEY-
 * touching (the paid path charges the chosen card server-side): the hook owns
 * NONE of the money logic — it forwards the body and lets the backend charge.
 *
 * On SUCCESS it invalidates the tournament-scoped registrations + eligibility
 * queries (registrationsMine + eligibility) so the per-event list
 * (TournamentEventList) refetches and the registered event's row flips to
 * "Registered" — the affirmation comes from the SERVER's refetched entry, never an
 * optimistic local flip. A FAILED register invalidates nothing (the row stays
 * "Register"), so a failed charge can never fabricate a registered state.
 *
 * `tournamentId` is needed ONLY to key the invalidation (the register endpoint
 * itself is event-scoped). The register dialog owns the pending/disabled gating
 * off `isPending`/`isSuccess` for the one-charge double-submit guard.
 */
export function useRegisterEntry(tournamentId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<
    TournamentPlayerRegistrationResponse,
    unknown,
    { eventId: string; body: EntryCreate }
  >({
    mutationFn: ({ eventId, body }) => tournamentApi.register(eventId, body),
    onSettled: () => queryClient.invalidateQueries({
      queryKey: queryKeys.tournaments.myRegistrationPayments(),
    }),
    onSuccess: async () => {
      const id = tournamentId ?? "";
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.registrationsMine(id),
      });
      // The verdict can change after registering (e.g. an EBC max-events /
      // duplicate-discipline cap now applies to the remaining events), so refetch
      // the verdicts too — the list greys newly-blocked rows from server truth.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.eligibility(id),
      });
      // The per-event aggregate entry_count lives on the events-list query, so a
      // successful entry/withdrawal must refetch it too — otherwise the row's
      // "N entries" count goes stale until a hard refresh.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.events(id),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.tournaments.myRegistrations(),
      });
    },
    onError: (error) => {
      if (isApiError(error) && error.code === "REGISTRATION_PRICE_CHANGED") {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.tournaments.eligibility(tournamentId ?? ""),
        });
      }
    },
  });
}
